import { graphql, useStaticQuery } from 'gatsby';

import { LOCALES_TO_LANGS } from '@/components/media/constants/media.constants';
import { MediaItemType } from '@/components/media/MediaItem';

interface Props {
  locales?: string[];
  tags?: string[];
  featuredItem: MediaItemType;
  isMediaWall: boolean;
}

const cleanString = (s) => s.toLowerCase().replace(/\s/g, '');

const mapLocalesToLangs = (locales) => {
  return locales.map((l) => LOCALES_TO_LANGS[l]);
};

const useMediaItems = (props?: Props): MediaItemType[] => {
  const { tags, locales, featuredItem, isMediaWall } = props || {};

  const { allContentfulMediaItem } = useStaticQuery(graphql`
    query MediaItemsQuery {
      allContentfulMediaItem(
        sort: { fields: createdAt, order: DESC }
        limit: 1000
      ) {
        nodes {
          ...MediaItemFragment
        }
      }
    }
  `);

  let items = allContentfulMediaItem?.nodes || [];

  if (!items.length) {
    return [];
  }

  // If filters are not displayed, we need to remove the media item
  // used as featuredItem from the list of items
  if (!isMediaWall && featuredItem) {
    const featuredItemIndex = items.findIndex(
      (i) => i.contentful_id === featuredItem.contentful_id,
    );

    if (featuredItemIndex > -1) {
      items.splice(featuredItemIndex, 1);
    }
  }

  // Filter elements based on languages they have been created for.
  // Media items are duplicated per locales, so we remove the ones
  // created by Contentful but not listed in languages field.
  items = items.filter((n) =>
    n.languages?.includes(LOCALES_TO_LANGS[n.node_locale]),
  );

  if (tags?.length) {
    const cleanedTags = tags.map((t) => cleanString(t));
    items = items.filter((i) =>
      i.tags?.some((t) => cleanedTags.includes(cleanString(t))),
    );
  }

  if (locales?.length) {
    const mappedLocales = mapLocalesToLangs(locales);

    items = items
      .filter((i) => i.languages.some((l) => mappedLocales.includes(l)))
      .filter((i) => locales.includes(i.node_locale));
  }

  return items;
};

export default useMediaItems;
